import { useCallback } from 'react';
import { motion } from 'framer-motion';
import { Upload, FileSpreadsheet, Download, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { generateCSVTemplate, batchQRTypes } from '@/hooks/useBatchJobs';

interface BatchUploadStepProps {
  onFileSelect: (file: File) => void;
  maxRows: number;
  isUploading: boolean;
}

export function BatchUploadStep({ onFileSelect, maxRows, isUploading }: BatchUploadStepProps) {
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file && file.name.endsWith('.csv')) {
        onFileSelect(file);
      }
    },
    [onFileSelect]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        onFileSelect(file);
      }
    },
    [onFileSelect]
  );

  const downloadTemplate = (type: string) => {
    const csv = generateCSVTemplate(type);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qr-batch-template-${type}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Template Download */}
      <div className="surface-elevated rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileSpreadsheet className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">Download CSV Template</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Choose a QR type to download a pre-formatted template with the correct columns.
        </p>
        <div className="flex flex-wrap gap-3">
          <Select onValueChange={downloadTemplate}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select QR Type" />
            </SelectTrigger>
            <SelectContent>
              {batchQRTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
              <SelectItem value="multi">Multi-Type Template</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => downloadTemplate('multi')}>
            <Download className="h-4 w-4 mr-2" />
            Download All-in-One Template
          </Button>
        </div>
      </div>

      {/* Upload Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className={`
          relative border-2 border-dashed rounded-xl p-12 text-center transition-colors
          ${isUploading ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
        `}
      >
        <input
          type="file"
          accept=".csv"
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isUploading}
        />
        
        {isUploading ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4" />
            <p className="text-lg font-medium">Processing file...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Upload className="h-8 w-8 text-primary" />
            </div>
            <p className="text-lg font-medium mb-2">
              Drag & drop your CSV file here
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse
            </p>
            <Button variant="outline">
              <Upload className="h-4 w-4 mr-2" />
              Select File
            </Button>
          </div>
        )}
      </div>

      {/* Limits Info */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
        <AlertCircle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-1">File Requirements</p>
          <ul className="space-y-1">
            <li>• CSV format only (.csv extension)</li>
            <li>• Maximum file size: 10 MB</li>
            <li>• Maximum rows: {maxRows} QR codes</li>
            <li>• Required columns: name, type, and type-specific fields</li>
          </ul>
        </div>
      </div>
    </motion.div>
  );
}
